/**
 * Client-side image resize helpers — used by photo meal logging and body-progress
 * photo uploads. Both flows resize to 1280px JPEG (quality 0.82) before sending
 * so the backend never receives multi-megabyte originals.
 */

const MAX_DIM = 1280;

/**
 * Read a File from a file picker → { base64, previewUrl, mime }
 * - base64: raw base64 string (NO data URL prefix). Safe to embed in JSON.
 * - previewUrl: full data URL — drop into <img src> for instant preview.
 */
export async function fileToResizedBase64(file, maxDim = MAX_DIM) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  return dataUrlToResizedBase64(dataUrl, maxDim);
}

/** Resize any data URL down to maxDim along the longer edge. */
export async function dataUrlToResizedBase64(dataUrl, maxDim = MAX_DIM) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  let { width, height } = img;
  const scale = Math.min(1, maxDim / Math.max(width, height));
  width = Math.round(width * scale);
  height = Math.round(height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  const out = canvas.toDataURL("image/jpeg", 0.82);
  const base64 = out.split(",")[1];
  return { base64, previewUrl: out, mime: "image/jpeg" };
}
